import { Box, Button, ButtonProps, Theme, Typography, useTheme } from "@mui/material";
import { Row } from "v2/components/base/Flex/ui";

type Variant = "primary" | "white" | "dark" | "disabled" | "green" | "red" | "black";

type VariantColor = {
  [key in Variant]: string;
};

const SURFACE_COLOR = (theme: Theme): VariantColor => ({
  primary: theme.palette.brand.primary,
  white: theme.palette.surface.light,
  disabled: theme.palette.surface.silver,
  dark: theme.palette.surface.container,
  green: theme.palette.system["green"],
  red: theme.palette.system["red"],
  black: theme.palette.surface.black,
});

const SURFACE_BACKGROUND_COLOR = (theme: Theme): VariantColor => ({
  primary: theme.palette.brand["primary"],
  white: theme.palette.surface.light,
  disabled: theme.palette.surface["container-bg"],
  dark: theme.palette.surface.bg,
  green: theme.palette.system["green"],
  red: theme.palette.system["red"],
  black: theme.palette.surface.black,
});

const TEXT_COLOR = (theme: Theme): VariantColor => ({
  primary: theme.palette.text.brand,
  white: theme.palette.text["btn-dark"],
  disabled: theme.palette.text.secondary,
  dark: theme.palette.text.primary,
  green: theme.palette.text["system-green"],
  red: theme.palette.text["system-red"],
  black: theme.palette.text.primary,
});

const BORDER_COLOR = (theme: Theme): VariantColor => ({
  primary: theme.palette.border.dark,
  white: theme.palette.border.light,
  disabled: theme.palette.border.light,
  dark: theme.palette.border.dark,
  green: theme.palette.border.dark,
  red: theme.palette.border.dark,
  black: theme.palette.border.white,
});

const BaseButton = ({
  children,
  ...props
}: {
  variant?: Variant;
  children: React.ReactNode;
} & ButtonProps) => {
  const theme = useTheme();
  const { sx, variant = "primary", ...rest } = props;

  return (
    <Button
      sx={{
        minWidth: "0px",
        border: `1px solid ${BORDER_COLOR(theme)[variant]}`,
        borderRadius: "16px",
        padding: "14.5px",
        backgroundColor: SURFACE_BACKGROUND_COLOR(theme)[variant],
        color: TEXT_COLOR(theme)[variant],
        "&:hover": {
          backgroundColor: SURFACE_BACKGROUND_COLOR(theme)[variant],
          filter: "brightness(0.9)",
        },
        transition: "all 0.3s ease-in-out",
        ...sx,
      }}
      {...rest}>
      {typeof children === "string" ? (
        <Typography variant="mobile-action-s-heavy" color="text.btn-dark">
          {children}
        </Typography>
      ) : (
        children
      )}
    </Button>
  );
};

export default BaseButton;
